import { motion } from "framer-motion";
import {
  PhoneOff, Timer, UserX, Headset, MessageSquareText, Filter,
  CalendarCheck, Repeat, HelpCircle, Star, Database, Clock,
  Zap, TrendingUp, ClipboardList, BellRing, PiggyBank, Quote, ArrowRight,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, Overline, SectionHeader, PrimaryButton } from "@/components/ui-bits";
import { EASE } from "@/components/ui-bits";

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32 ${className}`}>
    {children}
  </section>
);

/* ---------------- PROBLEM (numbered manifesto) ---------------- */
const PROBLEMS = [
  { icon: PhoneOff, title: "Unanswered calls walk away", body: "When the phone rings and no one picks up, that caller doesn't leave a voicemail — they call your competitor instead." },
  { icon: Timer, title: "Slow replies lose the sale", body: "Leads expect an answer in seconds, not hours. Every minute of silence dramatically lowers your chance of winning the job." },
  { icon: UserX, title: "Follow-ups never happen", body: "Busy days mean interested prospects slip through the cracks. Without consistent follow-up, real revenue quietly disappears." },
];

export function Problem() {
  return (
    <Section id="problem">
      <div className="pv-radial absolute inset-0 -z-10" />
      <SectionHeader
        overline="The Problem"
        title="Every missed call is lost revenue."
        sub="Service businesses lose real money when calls go unanswered, responses take too long, or leads are never followed up with. The work is out there — most of it is just slipping away."
      />
      <div className="mt-16 grid gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/5 md:grid-cols-3">
        {PROBLEMS.map((p, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className="group relative h-full bg-[#0C1120] p-8 transition-colors duration-300 hover:bg-[#141b2e] sm:p-10">
              <span className="font-heading text-7xl font-bold text-white/5 transition-colors duration-300 group-hover:text-[#00F0FF]/10">
                0{i + 1}
              </span>
              <p.icon className="mt-2 h-8 w-8 text-[#00F0FF]" strokeWidth={1.6} />
              <h3 className="mt-6 font-heading text-2xl font-medium tracking-tight">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- SERVICES (bento grid) ---------------- */
const SERVICE_IMG =
  "https://images.unsplash.com/photo-1733723586975-9aaae6983459?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGJsdWUlMjBnbG93aW5nJTIwbGluZXMlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc4NDA3MDAyNHww&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  { icon: Headset, title: "AI Receptionist", body: "A friendly, knowledgeable voice that answers every call and greets every lead — instantly." },
  { icon: MessageSquareText, title: "Missed-Call Text Back", body: "Any missed call triggers an instant, personalized text so the conversation never stops." },
  { icon: Filter, title: "Lead Qualification", body: "Asks the right questions to identify serious, ready-to-book prospects automatically." },
  { icon: CalendarCheck, title: "Appointment Booking", body: "Books estimates and appointments directly into your calendar in real time." },
  { icon: Repeat, title: "Automated Follow-Up", body: "Nurtures every lead with timely, consistent follow-ups so nothing goes cold." },
  { icon: HelpCircle, title: "FAQ Responses", body: "Answers your most common questions accurately, using details about your business." },
  { icon: Star, title: "Review Request Automation", body: "Requests reviews from happy customers at exactly the right moment." },
  { icon: Database, title: "CRM Lead Organization", body: "Captures and organizes every lead so your pipeline stays clean and clear." },
];

export function Services() {
  return (
    <Section id="services">
      <SectionHeader
        overline="What It Does"
        title="One AI employee. Every lead handled."
        sub="From the first ring to the booked appointment, Point Vision AI covers the work that keeps your business growing."
      />
      <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-3">
        <Reveal className="md:row-span-2">
          <div className="relative flex h-full min-h-[280px] flex-col justify-end overflow-hidden rounded-3xl border border-white/10 p-8">
            <img src={SERVICE_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-70" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0C1120] via-[#0C1120]/50 to-transparent" />
            <div className="relative z-10">
              <Overline>Always On</Overline>
              <h3 className="mt-4 font-heading text-3xl font-medium tracking-tight">Your business, always answered.</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">A complete AI workforce that captures, qualifies, and converts leads without a single missed opportunity.</p>
            </div>
          </div>
        </Reveal>
        {SERVICES.map((s, i) => (
          <Reveal key={i} delay={(i % 3) * 0.06}>
            <div className="group h-full rounded-3xl border border-white/10 bg-[#1B263B]/60 p-7 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-[#00F0FF]/40 hover:shadow-[0_0_30px_rgba(0,240,255,0.12)]">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#00F0FF]/10 transition-colors duration-300 group-hover:bg-[#00F0FF]/20">
                <s.icon className="h-5 w-5 text-[#00F0FF]" strokeWidth={1.8} />
              </span>
              <h3 className="mt-5 font-heading text-lg font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- HOW IT WORKS ---------------- */
const STEPS = [
  { n: "01", title: "We learn about your business", body: "We map your services, pricing, hours, and the questions your customers ask most." },
  { n: "02", title: "We build your AI employee", body: "We create and customize your AI to sound like your brand and follow your process." },
  { n: "03", title: "The AI starts responding & booking", body: "It answers leads, qualifies them, and books appointments directly into your calendar." },
  { n: "04", title: "You monitor everything in one place", body: "Track every conversation and appointment from a single, simple dashboard." },
];

export function HowItWorks() {
  return (
    <Section id="how-it-works">
      <div className="pv-radial absolute inset-0 -z-10" />
      <SectionHeader
        overline="How It Works"
        title="From setup to booked estimates in four steps."
      />
      <div className="mt-16 grid gap-8 lg:grid-cols-2">
        {STEPS.map((s, i) => (
          <Reveal key={i} delay={(i % 2) * 0.1}>
            <div className="group relative flex gap-6 rounded-3xl border border-white/10 bg-[#1B263B]/40 p-8 transition-colors duration-300 hover:border-[#00F0FF]/30 sm:p-10">
              <span className="font-heading text-5xl font-bold text-[#00F0FF]/30 transition-colors duration-300 group-hover:text-[#00F0FF] sm:text-6xl">{s.n}</span>
              <div>
                <h3 className="font-heading text-xl font-semibold sm:text-2xl">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400 sm:text-base">{s.body}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- BENEFITS ---------------- */
const BENEFITS = [
  { icon: Clock, title: "Available 24/7", body: "Every call and message is handled — day, night, weekends, and holidays." },
  { icon: Zap, title: "Responds within seconds", body: "Leads get an instant reply while their interest is still red-hot." },
  { icon: TrendingUp, title: "Books more estimates", body: "More conversations converted into scheduled appointments on your calendar." },
  { icon: ClipboardList, title: "Reduces admin work", body: "Less time on the phone and paperwork, more time doing the actual work." },
  { icon: BellRing, title: "Never forgets to follow up", body: "Consistent, automatic follow-ups keep every lead moving forward." },
  { icon: PiggyBank, title: "Costs less than a hire", body: "A fraction of the cost of another full-time employee — with no time off." },
];

export function Benefits() {
  return (
    <Section id="benefits">
      <SectionHeader
        overline="The Payoff"
        title="Why owners put Point Vision AI to work."
      />
      <div className="mt-16 grid gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/5 sm:grid-cols-2 lg:grid-cols-3">
        {BENEFITS.map((b, i) => (
          <Reveal key={i} delay={(i % 3) * 0.06}>
            <div className="group h-full bg-[#0C1120] p-8 transition-colors duration-300 hover:bg-[#141b2e]">
              <b.icon className="h-7 w-7 text-[#00F0FF]" strokeWidth={1.6} />
              <h3 className="mt-5 font-heading text-lg font-semibold">{b.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{b.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- OFFER ---------------- */
export function Offer() {
  return (
    <Section id="offer">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2rem] border border-[#00F0FF]/25 bg-gradient-to-br from-[#132040] to-[#0C1120] p-10 sm:p-16">
          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#00F0FF]/15 blur-[100px]" />
          <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#3A82FF]/15 blur-[100px]" />
          <div className="relative z-10 max-w-2xl">
            <Overline>Limited Offer</Overline>
            <h2 className="mt-5 font-heading text-4xl font-bold tracking-tight sm:text-5xl">
              Try Your AI Employee <span className="pv-cyan-gradient">Free</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-300 sm:text-lg">
              Qualified businesses can put Point Vision AI to work without paying for
              the first month. See the missed calls captured and the appointments
              booked — before you spend a dollar.
            </p>
            <div className="mt-9">
              <PrimaryButton
                data-testid="offer-claim-btn"
                onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
              >
                Claim Your Free Month <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </PrimaryButton>
            </div>
          </div>
        </div>
      </Reveal>
    </Section>
  );
}

/* ---------------- TESTIMONIALS (placeholders) ---------------- */
const T_IMG_1 = "https://images.unsplash.com/photo-1774528205285-53a125a51983?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBkYXJrfGVufDB8fHx8MTc4NDA3MDAyM3ww&ixlib=rb-4.1.0&q=85";
const T_IMG_2 = "https://images.unsplash.com/photo-1758599543125-0a927f1d7a3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBkYXJrfGVufDB8fHx8MTc4NDA3MDAyM3ww&ixlib=rb-4.1.0&q=85";

const TESTIMONIALS = [
  { img: T_IMG_1, name: "Placeholder Name", role: "Owner, Placeholder Business" },
  { img: T_IMG_2, name: "Placeholder Name", role: "Manager, Placeholder Business" },
  { img: null, name: "Placeholder Name", role: "Founder, Placeholder Business" },
];

export function Testimonials() {
  return (
    <Section id="testimonials">
      <SectionHeader
        overline="Placeholder Testimonials"
        title="Space for your future customer stories."
        sub="These are placeholder examples for you to replace with your own testimonials later. They are not verified customer reviews."
      />
      <div className="mt-16 grid gap-6 md:grid-cols-3">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="flex h-full flex-col rounded-3xl border border-white/10 bg-[#1B263B]/50 p-8">
              <span className="mb-4 inline-flex w-fit rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/5 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#00F0FF]">
                Placeholder
              </span>
              <Quote className="h-8 w-8 text-[#00F0FF]/40" />
              <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-300">
                “Add your customer's testimonial here. Describe how Point Vision AI
                helped capture missed leads and book more appointments. Replace this
                text with a real quote when you have one.”
              </p>
              <div className="mt-6 flex items-center gap-3 border-t border-white/10 pt-5">
                {t.img ? (
                  <img src={t.img} alt="" className="h-11 w-11 rounded-full object-cover" />
                ) : (
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#00F0FF]/10 font-heading text-sm font-bold text-[#00F0FF]">PV</span>
                )}
                <div>
                  <p className="text-sm font-semibold text-white">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.role}</p>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- FAQ ---------------- */
const FAQS = [
  { q: "Does the AI sound natural?", a: "Yes. It's designed to sound friendly and professional, using natural language and details about your business so conversations feel genuine." },
  { q: "Can it book directly into my calendar?", a: "Yes. Point Vision AI books estimates and appointments straight into your calendar in real time, so your schedule is always up to date." },
  { q: "Can I see the conversations?", a: "Absolutely. You can monitor every conversation and appointment from one simple dashboard whenever you like." },
  { q: "Does it work after business hours?", a: "Yes. It works 24/7 — nights, weekends, and holidays — so leads are always answered, even when you're off the clock." },
  { q: "Can it answer questions about my specific business?", a: "Yes. During setup we teach it about your services, pricing, policies, and the questions your customers ask most." },
  { q: "How long does setup take?", a: "Setup is quick. We learn about your business, build and customize your AI employee, and get you live without a lengthy onboarding process." },
  { q: "What happens after the free month?", a: "After your free month, you can choose to continue on a simple plan. There's no obligation — you only continue if it's working for you." },
];

export function Faq() {
  return (
    <Section id="faq">
      <div className="grid gap-14 lg:grid-cols-[0.8fr_1.2fr]">
        <div>
          <SectionHeader overline="Questions" title="Answers before you ask." />
          <Reveal delay={0.2}>
            <p className="mt-8 text-sm leading-relaxed text-slate-400">
              Still curious about something? Book a free demo and we'll walk you
              through exactly how it works for your business.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
                <AccordionTrigger className="text-left font-heading text-base font-medium text-white hover:text-[#00F0FF] hover:no-underline sm:text-lg">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-400 sm:text-base">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </Section>
  );
}
